import { AddressFieldMiddleware, useAddressFieldCompletion } from '@adornis/address/client/AddressFieldMiddleware.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { FieldPathDotSeparated } from '@adornis/baseql/entities/field-path-resolution.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import type { Form } from '@adornis/forms/form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { splitStreetAndNumber } from '../../../db/utils/util-address.js';
import type { RegisterStudentData } from '../api/registerStudentWithSuSForm.js';
import { engineFieldAttributes } from './fields/engine-field-attributes.js';
import type { RegistrationPath } from './fields/registration-field.js';

/**
 * The house number check misses valid lines (Mannheim's "Q7,12", "12 Hinterhaus"), so a line without a recognized
 * number is not rejected, it only has to be confirmed once by the applicant.
 */
export const needsAddressConfirmation = (line: Maybe<string>, confirmed: Maybe<string[]>) =>
  !!line?.trim() && !splitStreetAndNumber(line)[1] && !confirmed?.includes(line);

export const SgSusEntryFormAddressFields = stateful(
  (props: { form: Form<RegisterStudentData>; prefix?: RegistrationPath; guardian?: boolean }) => {
    const completion = useAddressFieldCompletion({ countries: ['de'], language: 'de' });
    const fields = [
      { key: 'streetAndNumber', label: 'Straße und Hausnummer', type: 'address' },
      { key: props.guardian ? 'zipSB' : 'zip', label: 'Postleitzahl', type: 'zip' },
      { key: props.guardian ? 'citySB' : 'city', label: 'Ort', type: 'city' },
      // optional - picking a street suggestion fills it only where Google knows the district
      { key: props.guardian ? 'districtSB' : 'district', label: 'Stadtteil', type: 'district' },
    ] as const;
    return html`<div class="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
      ${fields.map(({ key, label, type }) => {
        const path = [...(props.prefix ?? []), key];
        const id = `sus-${path.join('-')}`;
        const required = type !== 'district';
        return html`<div
          ${engineFieldAttributes({ id, name: path.join('.'), label, required })}
          class=${type === 'address' ? 'min-w-0 sm:col-span-2 [&_input]:text-base' : 'min-w-0 [&_input]:text-base'}
        >
          <label for=${id} class="mb-2 block text-sm font-medium">${label}${required ? ' *' : ''}</label>
          ${props.form.renderField<RegisterStudentData>(
            path.join('.') as FieldPathDotSeparated<RegisterStudentData>,
            field => {
              const line = typeof field.value === 'string' ? field.value : '';
              return html`${DropdownSelectionInput(
                AddressFieldMiddleware({
                  ...field,
                  onPicked: value => props.form.pickValueForField(path, value),
                  ariaLabel: label,
                  placeholder: label,
                  freeText: true,
                  fieldType: type,
                  addressCompletion: completion,
                }),
              )}${
                type === 'address' && needsAddressConfirmation(line, props.form.document?.confirmedAddresses)
                  ? html`<div
                      class="alert alert-warning alert-soft mt-2 flex flex-col items-start gap-3 text-base-content"
                      role="status"
                    >
                      <span>
                        Wir konnten keine Hausnummer erkennen. Bitte ergänzen Sie die Hausnummer oder bestätigen Sie,
                        dass die Adresse so stimmt.
                      </span>
                      <button
                        type="button"
                        class="btn btn-warning min-h-11"
                        @click=${() => {
                          props.form.pickValueForField(
                            ['confirmedAddresses'],
                            [...(props.form.document?.confirmedAddresses ?? []), line],
                          );
                          // picking the line again clears its "Hausnummer" error from the last submit
                          props.form.pickValueForField(path, line);
                        }}
                      >
                        Adresse stimmt
                      </button>
                    </div>`
                  : nothing
              }`;
            },
          )}
        </div>`;
      })}
    </div>`;
  },
);
